import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { StateService } from '@services/Logic/state.service';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';

interface Step {
  key: string;
  state: 'LOADING' | 'COMPLETED';
  icon: string;
  progress?: number; // Progress as a percentage
}

@Component({
  selector: 'app-extended-waiting-module-scoped',
  templateUrl: './extendedWaitingModule.component.html',
  styleUrls: ['./extendedWaitingModule.component.scss'],
})
export class ExtendedWaitingModuleComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() displayExtendedLoader: boolean = false;
  @Output() doneLoading: EventEmitter<any> = new EventEmitter<any>();

  isVisible: boolean = false; // This controls the visibility of the component
  private _doneLoadingSteps = false; // Private backing field

  // Getter for _doneLoadingSteps to control external access
  get doneLoadingSteps(): boolean {
    return this._doneLoadingSteps;
  }

  // Setter for _doneLoadingSteps that includes logic to complete the loading process when appropriate
  set doneLoadingSteps(value: boolean) {
    this._doneLoadingSteps = value;
    // Place your additional logic here
    if (value && !this.displayExtendedLoader && this.doneLoadingSteps) {
      this.completeLoadingProcess();
    }
  }
  currentLoadingStep = 'Comparing_Tasks'; // Dynamically updated based on logic to indicate current step
  layersIcon = `${environment.basePath}assets/svg/create_task_icons/layers.svg`; // Path to the icon used in the loading steps
  currentStepIndex = 0; // Tracks the current step index
  awaitAnimation = true; // Controls the animation display
  title: string = this.translatePipe.transform(
    'LOADING_TRANSITION_PLACEHOLDERS.EXTENDED_LOADER_DEFAULT_HEADLINE',
  ); // Translated title for the loader
  steps: Step[] = [
    { key: 'ANALYZING_TASK_TYPE', state: 'LOADING', icon: this.layersIcon },
  ]; // Array of steps involved in the loading process
  showTimeOutError = false; // Flag to show a timeout error

  private timeoutRef: any; // Holds a reference to the timeout for automatic completion
  showCompleteAnimation = false; // Controls the display of the completion animation
  private subscriptions = new Subscription(); // Holds subscriptions to avoid memory leaks

  private _isLoadingInProgress: boolean = false; // Tracks whether loading is currently in progress

  // Constructor to inject dependencies and setup initial state
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stateService: StateService,
    private translatePipe: TranslatePipe,
  ) {
    // Subscribe to changes in the extended waiting component's state
    this.subscriptions.add(
      this.stateService.toggleExtendedWaitingComponentSource.subscribe(
        ({ status, title, steps }) => {
          if (this._isLoadingInProgress) {
            return; // Ignore if already loading
          }
          if (status) {
            this.isVisible = status;
            this._isLoadingInProgress = true;
            this.updateStepStatus();
          }
          if (steps && steps.length) {
            this.populateStepsFromArray(steps);
          }
          if (title) {
            this.title = title;
          }
        },
      ),
    );
  }

  // Lifecycle hook for initialization logic
  ngOnInit() {
    this.debugTrackWaitingByQueryParam();
    this.currentLoadingStep = this.steps.length > 0 ? this.steps[0].key : null;
    if (this.displayExtendedLoader) {
      this.isVisible = true;
    }
    // Set a timeout to force completion after a specified duration
    this.timeoutRef = setTimeout(() => {
      this.completeLoadingProcess(true);
    }, 25000);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Logic to complete loading process based on changes to inputs
    if (
      changes.displayExtendedLoader &&
      !this.displayExtendedLoader &&
      this.doneLoadingSteps
    ) {
      // If displayExtendedLoader is set to false, check if there are steps still loading
      this.completeLoadingProcess();
    }
    if (
      changes.doneLoadingSteps &&
      !this.displayExtendedLoader &&
      this.doneLoadingSteps
    ) {
      this.completeLoadingProcess();
    }
  }

  // Lifecycle hook for cleanup to prevent memory leaks
  ngOnDestroy() {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
    this.subscriptions.unsubscribe();
  }

  private debugTrackWaitingByQueryParam() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        loader: 'pending',
      },
      queryParamsHandling: 'merge',
    });
  }

  // Updates the steps array from an input array, setting initial state and progress
  populateStepsFromArray(inputArray: any[]): void {
    // Logic to populate the steps of the loading process
    if (inputArray && inputArray.length > 0) {
      this.steps = inputArray.map((item) => ({
        key: item.key,
        state: 'LOADING', // Default state
        icon: item.icon,
        progress: item.progress || 0, // Default progress
      }));
      if (this.steps.length > 0) {
        this.currentLoadingStep = this.steps[0].key;
      }

      this.updateStepStatus();
    } else {
      console.log('No steps to update.');
    }
  }

  // Updates the status and progress of each loading step periodically
  updateStepStatus() {
    // Logic to update each step's progress and state
    const interval = 80;
    const progressIncrement = 2;
    const updateSteps = () => {
      if (this.currentStepIndex < this.steps.length) {
        const currentStep = this.steps[this.currentStepIndex];
        if (currentStep.progress === undefined) {
          currentStep.progress = 0;
        }
        currentStep.progress = Math.min(
          currentStep.progress + progressIncrement,
          100,
        );

        if (currentStep.progress >= 100) {
          currentStep.state = 'COMPLETED';
          this.currentStepIndex++;
          if (this.currentStepIndex < this.steps.length) {
            this.currentLoadingStep = this.steps[this.currentStepIndex].key;
            this.steps[this.currentStepIndex].progress = 0;
          } else {
            this.doneLoadingSteps = true;
          }
        }
        setTimeout(updateSteps, interval);
      }
    };
    setTimeout(updateSteps, interval);
  }

  // Completes the loading process, handling both normal completion and timeout errors
  completeLoadingProcess(timeOutError: boolean = false) {
    // Logic to mark the loading process as complete and emit an event
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
      this.timeoutRef = null;
    }

    if (timeOutError) {
      this.showTimeOutError = true;
      setTimeout(() => {
        this.doneLoading.emit(true);
        this.showCompleteAnimation = false;
        this.isVisible = false; // Hide the component
      }, 3000); // Duration for the complete animation
    } else {
      // Existing logic to complete the loading process
      this.showCompleteAnimation = true;
      setTimeout(() => {
        this.awaitAnimation = false;
        setTimeout(() => {
          this.doneLoading.emit(true);
          this.showCompleteAnimation = false;
          this._isLoadingInProgress = false; // Mark loading as complete
          this.isVisible = false; // Ensure to update visibility status
        }, 3000); // Duration for the complete animation
      }, 500); // Duration for the complete animation
    }
  }

  // Returns a display string for each step based on its state and translation keys
  getStepDisplayValue(step: Step): string {
    const keySuffix = step.state === 'LOADING' ? 'LOADING' : 'COMPLETED';
    const translationKey = `LOADING_TRANSITION_PLACEHOLDERS.LOADING_STEPS.${keySuffix}.${step.key}`;
    return this.translatePipe.transform(translationKey);
  }
}
